// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Catch Pass  (route /catch-pass — "Catch Pass", linked from the profile page; sign-in required)
// Regular boxes: the customer saves a plan — catches with cut and pack sizes, a rhythm (weekly / every 2 weeks / monthly) and a service
// day (Wed / Sat / Sun, Terms §9.3) — and adds the whole box to their cart in one tap (utils/plans → backend `customers/{uid}/plans`,
// max 5). It is NOT an auto-billed subscription: nothing is charged and no order is created for them; they confirm and pay at checkout
// like any order. "Due" is an in-app reminder from the last time the box was added; a paused plan never nags. Prices are the menu's today.
// Starter ideas are just prefilled builders (my own combinations of real catches, at real prices).
// Deliberately NOT here (no data/system behind it): a "VIP Seafood Club" tier, 15–22 % savings and strike-through prices, "zero delivery
// surcharges", free monthly prawns, "priority dawn fishmonger cuts", sub-zero/thermocool/dry-ice claims, sashimi-grade options, a chef's
// masala pouch, landing windows at fixed clock times, gifting/skipping with one click, species swaps until Thursday, an odour-free
// replacement guarantee, a 30-day risk-free trial, automatic weekly billing. Real recurring billing needs Razorpay Subscriptions / UPI
// AutoPay plus pricing and discount decisions the business hasn't made.
// Neighbours: components/catch/CatchPicker, utils/picks, hooks/useCart, pages/Checkout.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowLeft, BellRing, CalendarClock, CircleAlert, Loader2, Pause, Pencil, Play, Plus, ShoppingCart, Sparkles, Trash2 } from "lucide-react";
import Breadcrumbs from "../components/Breadcrumbs";
import PageTransition from "../components/PageTransition";
import CatchPicker from "../components/catch/CatchPicker";
import { defaultCut } from "../data/cuts";
import { seafoodItems } from "../data/seafood";
import useAuth from "../hooks/useAuth";
import useCart from "../hooks/useCart";
import { MAX_PACKS } from "../hooks/useCatchDraft";
import useDocumentTitle from "../hooks/useDocumentTitle";
import { ApiError } from "../utils/api";
import { formatWhen } from "../utils/orders";
import { linesToPicks, linesPrice, picksToLines, summarise, totals, type Picks } from "../utils/picks";
import {
  CADENCES,
  DAYS,
  MAX_PLANS,
  cadenceLabel,
  createPlan,
  dayLabel,
  deletePlan,
  fetchPlans,
  isDue,
  markPlanAdded,
  nextDue,
  setPlanPaused,
  updatePlan,
  type Cadence,
  type Day,
  type Plan,
  type PlanLine,
} from "../utils/plans";
import { formatInr } from "../utils/priceFormat";

const card = "rounded-2xl bg-white p-4 shadow-sm ring-1 ring-ocean-900/5 dark:bg-ocean-900 dark:ring-white/10";

interface Starter {
  name: string;
  blurb: string;
  lines: { id: string; pack: "500g" | "1kg"; qty: number }[];
}

// Ideas to start from — combinations of real catches at the menu's real prices (no discount).
const STARTERS: Starter[] = [
  { name: "Everyday curry box", blurb: "Two staples for the week's curries", lines: [{ id: "karimeen-pearlspot", pack: "500g", qty: 2 }, { id: "indian-mackerel", pack: "1kg", qty: 1 }] },
  { name: "Weekend fry box", blurb: "A firm fish and a pomfret for the tawa", lines: [{ id: "vanjaram-kingfish", pack: "1kg", qty: 1 }, { id: "silver-pomfret", pack: "500g", qty: 2 }] },
  { name: "Shellfish lovers", blurb: "Prawns and crab for a feast", lines: [{ id: "tiger-prawns", pack: "1kg", qty: 1 }, { id: "mud-crab", pack: "1kg", qty: 1 }] },
];

const startersToLines = (starter: Starter): PlanLine[] =>
  starter.lines.flatMap((line) => {
    const item = seafoodItems.find((candidate) => candidate.id === line.id);
    return item ? [{ catchId: item.id, pack: line.pack, qty: line.qty, cut: defaultCut(item).id }] : [];
  });

interface Draft {
  id: string | null;
  name: string;
  cadence: Cadence;
  day: Day;
  picks: Picks;
}

export default function CatchPass() {
  useDocumentTitle("Catch Pass");
  const auth = useAuth();
  const signedIn = auth.status === "signed-in";
  const { qtyOf, setQty } = useCart();
  const [plans, setPlans] = useState<Plan[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const formHeading = useRef<HTMLHeadingElement>(null);

  const load = async () => {
    setFailed(false);
    try {
      setPlans(await fetchPlans());
    } catch {
      setFailed(true);
    }
  };

  useEffect(() => {
    if (signedIn) void load();
  }, [signedIn]);

  useEffect(() => {
    if (draft) formHeading.current?.focus();
  }, [draft]);

  if (auth.status === "signed-out") return <Navigate to="/welcome" replace />;

  const atLimit = (plans?.length ?? 0) >= MAX_PLANS;
  const startDraft = (next: Partial<Draft> = {}) => {
    setError(null);
    setNotice(null);
    setDraft({ id: null, name: "", cadence: "weekly", day: "saturday", picks: {}, ...next });
  };
  const editPlan = (plan: Plan) => startDraft({ id: plan.id, name: plan.name, cadence: plan.cadence, day: plan.day, picks: linesToPicks(plan.lines) });

  const guard = async (key: string, work: () => Promise<void>, fallback: string) => {
    setBusy(key);
    setError(null);
    try {
      await work();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : fallback);
    } finally {
      setBusy(null);
    }
  };

  const save = () => {
    if (!draft) return;
    const lines = picksToLines(draft.picks);
    if (!draft.name.trim()) return setError("Give the box a name.");
    if (lines.length === 0) return setError("Add at least one catch to the box.");
    void guard(
      "save",
      async () => {
        const input = { name: draft.name.trim(), cadence: draft.cadence, day: draft.day, lines };
        if (draft.id) await updatePlan(draft.id, input);
        else await createPlan(input);
        setDraft(null);
        await load();
        setNotice(draft.id ? "Your box was updated." : "Your box was saved.");
      },
      "We couldn't save that box. Please try again.",
    );
  };

  const addToCart = (plan: Plan) =>
    void guard(
      plan.id,
      async () => {
        for (const line of plan.lines) {
          if (!seafoodItems.some((item) => item.id === line.catchId)) continue;
          setQty(line.catchId, line.pack, Math.min(MAX_PACKS, qtyOf(line.catchId, line.pack) + line.qty), line.cut ?? undefined);
        }
        await markPlanAdded(plan.id);
        await load();
        setNotice(`“${plan.name}” was added to your cart. Review it and pay at checkout.`);
      },
      "We couldn't add that box. Please try again.",
    );

  const togglePause = (plan: Plan) => void guard(plan.id, async () => { await setPlanPaused(plan.id, plan.status === "active"); await load(); }, "We couldn't change that plan.");
  const remove = (plan: Plan) => void guard(plan.id, async () => { await deletePlan(plan.id); setConfirmDelete(null); await load(); setNotice("The box was deleted."); }, "We couldn't delete that box.");

  const dueCount = (plans ?? []).filter((p) => isDue(p)).length;

  return (
    <PageTransition>
      <div className="bg-foam-50 pb-24 pt-28 dark:bg-ocean-950 sm:pt-32">
        <Breadcrumbs />
        <div className="mx-auto flex max-w-xl flex-col gap-4 px-4 pt-2">
          <header className="flex items-center gap-3">
            <Link to="/profile" aria-label="Back to my profile" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-ocean-900 shadow-sm ring-1 ring-ocean-900/10 hover:bg-ocean-700/5 dark:bg-ocean-900 dark:text-foam-100 dark:ring-white/10">
              <ArrowLeft className="h-5 w-5" aria-hidden="true" />
            </Link>
            <div className="min-w-0 flex-1">
              <h1 className="font-display text-2xl font-bold text-ocean-950 dark:text-foam-50">Catch Pass</h1>
              <p className="text-xs text-ocean-800/80 dark:text-foam-100/75">Your regular boxes, ready in one tap</p>
            </div>
          </header>

          <Link to="/coming-soon/vip-club" className="flex min-h-12 items-center justify-between gap-3 rounded-2xl bg-coral-500/10 px-4 py-3 text-sm font-bold text-ocean-950 ring-1 ring-coral-500/30 dark:text-foam-50">
            <span>Catch Pass VIP Seafood Club</span>
            <span className="shrink-0 rounded-full bg-coral-500/20 px-2 py-0.5 text-[10px] text-coral-600 dark:text-coral-300">Coming soon</span>
          </Link>
          {!draft && (
            <section className="rounded-2xl bg-ocean-900 p-4 text-foam-50 shadow-md">
              <h2 className="font-display text-lg font-bold leading-snug">Set your box once. Add it whenever you&apos;re ready.</h2>
              <ul className="mt-2 space-y-1.5 text-sm text-foam-100/90">
                <li>Pick the catches, cuts and pack sizes you order regularly.</li>
                <li>Choose how often and on which service day — we&apos;ll remind you here when it&apos;s due.</li>
                <li><strong className="text-foam-50">Nothing is charged automatically.</strong> You add the box to your cart and pay at checkout each time.</li>
              </ul>
            </section>
          )}

          <p role="status" className="min-h-5 text-sm font-semibold text-teal-700 dark:text-teal-300">{notice}</p>
          <p role="alert" className="flex min-h-5 items-center gap-1.5 text-sm text-coral-600 dark:text-coral-300">
            {error && <CircleAlert className="h-4 w-4 shrink-0" aria-hidden="true" />}
            {error}
          </p>

          {draft ? (
            <form
              className="flex flex-col gap-4"
              onSubmit={(event) => {
                event.preventDefault();
                save();
              }}
              noValidate
            >
              <h2 ref={formHeading} tabIndex={-1} className="font-display text-xl font-bold text-ocean-950 outline-none dark:text-foam-50">{draft.id ? "Edit box" : "New box"}</h2>
              <div className={card}>
                <label htmlFor="plan-name" className="text-xs font-bold text-ocean-900 dark:text-foam-50">Name your box</label>
                <input id="plan-name" value={draft.name} maxLength={40} onChange={(event) => setDraft({ ...draft, name: event.target.value })} placeholder="e.g. Weekend fry box" className="mt-1 min-h-11 w-full rounded-xl bg-white px-3 text-sm text-ocean-950 ring-1 ring-ocean-900/15 focus-visible:outline-2 focus-visible:outline-teal-600 dark:bg-ocean-950 dark:text-foam-50" />
                <p id="cad-l" className="mt-3 text-xs font-bold text-ocean-900 dark:text-foam-50">How often</p>
                <div role="radiogroup" aria-labelledby="cad-l" className="mt-1 grid grid-cols-3 gap-2">
                  {CADENCES.map((option) => (
                    <button key={option.id} type="button" role="radio" aria-checked={draft.cadence === option.id} onClick={() => setDraft({ ...draft, cadence: option.id })} className={`min-h-11 rounded-xl px-2 text-xs font-bold focus-visible:outline-2 focus-visible:outline-teal-600 ${draft.cadence === option.id ? "bg-ocean-800 text-white" : "bg-ocean-700/10 text-ocean-900 dark:bg-white/10 dark:text-foam-50"}`}>{option.label}</button>
                  ))}
                </div>
                <p id="day-l" className="mt-3 text-xs font-bold text-ocean-900 dark:text-foam-50">Service day</p>
                <div role="radiogroup" aria-labelledby="day-l" className="mt-1 grid grid-cols-3 gap-2">
                  {DAYS.map((option) => (
                    <button key={option.id} type="button" role="radio" aria-checked={draft.day === option.id} onClick={() => setDraft({ ...draft, day: option.id })} className={`min-h-11 rounded-xl px-2 text-xs font-bold focus-visible:outline-2 focus-visible:outline-teal-600 ${draft.day === option.id ? "bg-ocean-800 text-white" : "bg-ocean-700/10 text-ocean-900 dark:bg-white/10 dark:text-foam-50"}`}>{option.label}</button>
                  ))}
                </div>
                <p className="mt-1 text-[11px] text-ocean-800/80 dark:text-foam-100/75">Our service days are Wednesday, Saturday and Sunday, delivering between 10 a.m. and 1 p.m. (indicative — Terms §9.3).</p>
              </div>
              <CatchPicker picks={draft.picks} onChange={(picks) => setDraft({ ...draft, picks })} idPrefix="plan" />
              <div className="sticky bottom-3 z-30 flex items-center justify-between gap-3 rounded-2xl bg-white/95 p-3 shadow-lg ring-1 ring-ocean-900/10 backdrop-blur-md dark:bg-ocean-900/95 dark:ring-white/10">
                <div>
                  <p className="font-display text-lg font-bold text-ocean-950 dark:text-foam-50" data-testid="draft-total">{formatInr(totals(draft.picks).price)}</p>
                  <p className="text-[11px] text-ocean-800/80 dark:text-foam-100/75">per box, at today&apos;s menu prices</p>
                </div>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setDraft(null)} className="min-h-11 rounded-full bg-ocean-700/10 px-4 text-sm font-bold text-ocean-900 dark:bg-white/10 dark:text-foam-50">Cancel</button>
                  <button type="submit" disabled={busy === "save"} className="inline-flex min-h-11 items-center gap-2 rounded-full bg-ocean-800 px-5 text-sm font-bold text-white shadow-md hover:bg-ocean-700 disabled:opacity-60">
                    {busy === "save" && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />} Save box
                  </button>
                </div>
              </div>
            </form>
          ) : (
            <>
              {plans === null && !failed && <p role="status" className="flex items-center justify-center gap-2 text-ocean-900 dark:text-foam-50"><Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> Loading your boxes…</p>}
              {failed && (
                <div role="alert" className={`${card} text-center`}>
                  <p className="font-semibold text-ocean-950 dark:text-foam-50">We couldn&apos;t load your boxes.</p>
                  <button type="button" onClick={() => void load()} className="mt-3 min-h-11 rounded-full bg-ocean-800 px-6 font-bold text-white">Try again</button>
                </div>
              )}

              {dueCount > 0 && (
                <p role="status" className="flex items-center gap-2 rounded-xl bg-coral-500/10 p-3 text-sm font-bold text-ocean-950 dark:text-foam-50" data-testid="due-banner">
                  <BellRing className="h-5 w-5 shrink-0 text-coral-600 dark:text-coral-300" aria-hidden="true" /> {dueCount === 1 ? "One of your boxes is due." : `${dueCount} of your boxes are due.`}
                </p>
              )}

              {plans !== null && plans.length > 0 && (
                <section aria-labelledby="mine-h" className="flex flex-col gap-3">
                  <div className="flex items-center justify-between">
                    <h2 id="mine-h" className="font-display text-lg font-bold text-ocean-950 dark:text-foam-50">Your boxes</h2>
                    <span className="text-xs text-ocean-800/80 dark:text-foam-100/75">{plans.length} of {MAX_PLANS}</span>
                  </div>
                  <ul className="flex flex-col gap-3">
                    {plans.map((plan) => {
                      const due = isDue(plan);
                      const paused = plan.status === "paused";
                      return (
                        <li key={plan.id} className={card} data-testid="plan-card">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <h3 className="font-display text-lg font-bold text-ocean-950 dark:text-foam-50">{plan.name}</h3>
                              <p className="text-xs text-ocean-800/80 dark:text-foam-100/75">{cadenceLabel(plan.cadence)} · {dayLabel(plan.day)}s</p>
                            </div>
                            <span className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold ${paused ? "bg-ocean-700/10 text-ocean-900 dark:bg-white/10 dark:text-foam-50" : due ? "bg-coral-500/10 text-coral-600 dark:text-coral-300" : "bg-teal-100 text-teal-700"}`}>{paused ? "Paused" : due ? "Due now" : "Active"}</span>
                          </div>
                          <ul className="mt-2 space-y-0.5 rounded-xl bg-ocean-700/5 p-3 text-xs text-ocean-900 dark:bg-white/5 dark:text-foam-50">
                            {summarise(plan.lines).map((row) => <li key={row}>{row}</li>)}
                          </ul>
                          <p className="mt-2 flex items-baseline justify-between text-sm">
                            <span className="text-ocean-800/80 dark:text-foam-100/75">At today&apos;s menu prices</span>
                            <span className="font-display text-lg font-bold text-ocean-950 dark:text-foam-50">{formatInr(linesPrice(plan.lines))}</span>
                          </p>
                          <p className="mt-1 flex items-center gap-1.5 text-xs text-ocean-800/80 dark:text-foam-100/75">
                            <CalendarClock className="h-3.5 w-3.5" aria-hidden="true" />
                            {plan.lastAddedAt ? `Last added ${formatWhen(plan.lastAddedAt)}` : "Not added to your cart yet"}
                            {!paused && ` · next due ${nextDue(plan).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" })}`}
                          </p>
                          {confirmDelete === plan.id ? (
                            <div role="alertdialog" aria-label={`Delete ${plan.name}?`} className="mt-3 rounded-xl bg-coral-500/10 p-3">
                              <p className="text-sm font-semibold text-ocean-950 dark:text-foam-50">Delete this box?</p>
                              <div className="mt-2 flex gap-2">
                                <button type="button" onClick={() => remove(plan)} disabled={busy === plan.id} className="min-h-11 rounded-full bg-coral-600 px-4 text-sm font-bold text-white disabled:opacity-60">Yes, delete</button>
                                <button type="button" onClick={() => setConfirmDelete(null)} className="min-h-11 rounded-full bg-ocean-700/10 px-4 text-sm font-bold text-ocean-900 dark:bg-white/10 dark:text-foam-50">Keep it</button>
                              </div>
                            </div>
                          ) : (
                            <div className="mt-3 flex flex-wrap gap-2">
                              <button type="button" onClick={() => addToCart(plan)} disabled={busy === plan.id} className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-full bg-ocean-800 px-4 text-sm font-bold text-white shadow-md hover:bg-ocean-700 disabled:opacity-60">
                                {busy === plan.id ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <ShoppingCart className="h-4 w-4" aria-hidden="true" />} Add box to cart<span className="sr-only"> — {plan.name}</span>
                              </button>
                              <button type="button" onClick={() => editPlan(plan)} aria-label={`Edit ${plan.name}`} className="flex h-11 w-11 items-center justify-center rounded-full bg-ocean-700/10 text-ocean-900 dark:bg-white/10 dark:text-foam-50"><Pencil className="h-4 w-4" aria-hidden="true" /></button>
                              <button type="button" onClick={() => togglePause(plan)} aria-label={paused ? `Resume ${plan.name}` : `Pause ${plan.name}`} className="flex h-11 w-11 items-center justify-center rounded-full bg-ocean-700/10 text-ocean-900 dark:bg-white/10 dark:text-foam-50">{paused ? <Play className="h-4 w-4" aria-hidden="true" /> : <Pause className="h-4 w-4" aria-hidden="true" />}</button>
                              <button type="button" onClick={() => setConfirmDelete(plan.id)} aria-label={`Delete ${plan.name}`} className="flex h-11 w-11 items-center justify-center rounded-full bg-coral-500/10 text-coral-600 dark:text-coral-300"><Trash2 className="h-4 w-4" aria-hidden="true" /></button>
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </section>
              )}

              {plans !== null && (
                <button type="button" disabled={atLimit} onClick={() => startDraft()} className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-ocean-800 px-6 font-bold text-white shadow-md hover:bg-ocean-700 disabled:opacity-50">
                  <Plus className="h-5 w-5" aria-hidden="true" /> {plans.length === 0 ? "Create your first box" : "Create another box"}
                </button>
              )}
              {atLimit && <p className="text-center text-sm text-ocean-800/85 dark:text-foam-100/80">You&apos;ve saved the maximum of {MAX_PLANS} boxes. Delete one to add another.</p>}

              {plans !== null && (
                <section aria-labelledby="starters-h" className="flex flex-col gap-2">
                  <h2 id="starters-h" className="flex items-center gap-2 font-display text-lg font-bold text-ocean-950 dark:text-foam-50"><Sparkles className="h-5 w-5" aria-hidden="true" /> Starter ideas</h2>
                  <ul className="flex flex-col gap-2">
                    {STARTERS.map((starter) => {
                      const lines = startersToLines(starter);
                      return (
                        <li key={starter.name} className={card} data-testid="starter">
                          <p className="font-display text-base font-bold text-ocean-950 dark:text-foam-50">{starter.name}</p>
                          <p className="text-xs text-ocean-800/80 dark:text-foam-100/75">{starter.blurb}</p>
                          <ul className="mt-2 space-y-0.5 text-xs text-ocean-900 dark:text-foam-50">{summarise(lines).map((row) => <li key={row}>{row}</li>)}</ul>
                          <div className="mt-2 flex items-center justify-between">
                            <span className="font-display text-lg font-bold text-ocean-950 dark:text-foam-50">{formatInr(linesPrice(lines))}</span>
                            <button type="button" disabled={atLimit} onClick={() => startDraft({ name: starter.name, picks: linesToPicks(lines) })} className="min-h-11 rounded-full bg-ocean-700/10 px-4 text-sm font-bold text-ocean-900 disabled:opacity-50 dark:bg-white/10 dark:text-foam-50">Start from this<span className="sr-only"> — {starter.name}</span></button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                  <p className="text-xs text-ocean-800/80 dark:text-foam-100/75">Prices are the menu&apos;s today — Catch Pass boxes carry no discount.</p>
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </PageTransition>
  );
}
